import { htmlHasVisibleText } from '@/lib/landing-html'

export type Template3Advantage = {
  title: string
  description: string
  image_url: string | null
}

export type Template3FaqItem = {
  question: string
  answer: string
}

export const TEMPLATE3_ADVANTAGES_COUNT = 3
export const TEMPLATE3_FAQ_COUNT = 3

export function emptyTemplate3Advantages(): Template3Advantage[] {
  return Array.from({ length: TEMPLATE3_ADVANTAGES_COUNT }, () => ({
    title: '',
    description: '',
    image_url: null,
  }))
}

export function emptyTemplate3Faq(): Template3FaqItem[] {
  return Array.from({ length: TEMPLATE3_FAQ_COUNT }, () => ({
    question: '',
    answer: '',
  }))
}

export function normalizeTemplate3Advantages(value: unknown): Template3Advantage[] {
  const source = Array.isArray(value) ? value : []
  return emptyTemplate3Advantages().map((fallback, index) => {
    const item = source[index]
    if (!item || typeof item !== 'object') {
      return fallback
    }

    const record = item as Record<string, unknown>
    return {
      title: typeof record.title === 'string' ? record.title : '',
      description: typeof record.description === 'string' ? record.description : '',
      image_url: typeof record.image_url === 'string' && record.image_url.trim() !== '' ? record.image_url : null,
    }
  })
}

export function normalizeTemplate3Faq(value: unknown): Template3FaqItem[] {
  const source = Array.isArray(value) ? value : []
  return emptyTemplate3Faq().map((fallback, index) => {
    const item = source[index]
    if (!item || typeof item !== 'object') {
      return fallback
    }

    const record = item as Record<string, unknown>
    return {
      question: typeof record.question === 'string' ? record.question : '',
      answer: typeof record.answer === 'string' ? record.answer : '',
    }
  })
}

export type Template3CampaignValidationInput = {
  template3_eyebrow?: string | null
  template3_about_title?: string | null
  template3_about_text?: string | null
  template3_advantages?: Array<{
    title?: string | null
    description?: string | null
    image_url?: string | null
  }> | null
  template3_faq?: Array<{
    question?: string | null
    answer?: string | null
  }> | null
  template3_hero_background?: File | null
  template3_about_image?: File | null
  template3_advantage_image_1?: File | null
  template3_advantage_image_2?: File | null
  template3_advantage_image_3?: File | null
  template3_hero_background_url?: string | null
  template3_about_image_url?: string | null
  template3_floating_cta_image?: File | null
  template3_floating_cta_image_url?: string | null
}

function hasImage(file: File | null | undefined, url: string | null | undefined): boolean {
  return Boolean(file) || Boolean(url?.trim())
}

/** Aceita só http(s) ou caminho relativo. Bloqueia javascript:, data: e URL protocol-relative. */
export function sanitizeTemplate3PublicImageSrc(url: string | null | undefined): string | null {
  if (!url) return null
  const trimmed = url.trim()
  if (trimmed === '') return null

  const lower = trimmed.toLowerCase()
  if (
    lower.startsWith('javascript:') ||
    lower.startsWith('data:') ||
    lower.startsWith('vbscript:') ||
    lower.startsWith('file:')
  ) {
    return null
  }

  if (trimmed.startsWith('//')) {
    return null
  }

  if (trimmed.startsWith('/') || lower.startsWith('https://') || lower.startsWith('http://')) {
    return trimmed
  }

  return null
}

export type Template3MissingFieldKey =
  | 'eyebrow'
  | 'aboutTitle'
  | 'aboutText'
  | 'heroBackground'
  | 'aboutImage'
  | 'floatingCta'
  | 'advantage-1'
  | 'advantage-2'
  | 'advantage-3'
  | 'faq-1'
  | 'faq-2'
  | 'faq-3'

export type Template3MissingField = {
  key: Template3MissingFieldKey
  message: string
}

const TEMPLATE3_MISSING_MESSAGES = {
  eyebrow: 'Informe a frase pequena que aparece acima do título no topo da página.',
  aboutTitle: 'Informe o título da seção Sobre o sorteio.',
  aboutText: 'Informe o texto da seção Sobre o sorteio.',
  advantageText: 'Cada bloco de Por que participar precisa de um título e uma descrição.',
  faq: 'Preencha as 3 dúvidas frequentes do final da página.',
  heroBackground: 'Envie a foto de fundo do topo da página.',
  aboutImage: 'Envie a foto da seção Sobre o sorteio.',
  floatingCta: 'Envie a imagem do botão flutuante de compra, no canto inferior direito.',
  advantageImage: 'Cada bloco de Por que participar precisa de uma foto.',
} as const

function advantageFieldKey(index: number): 'advantage-1' | 'advantage-2' | 'advantage-3' {
  if (index === 0) return 'advantage-1'
  if (index === 1) return 'advantage-2'
  return 'advantage-3'
}

/** Lista estável dos campos/grupos do Template 3 que ainda faltam no painel. */
export function listMissingTemplate3Fields(
  input: Template3CampaignValidationInput
): Template3MissingField[] {
  const missing: Template3MissingField[] = []

  if (!htmlHasVisibleText(input.template3_eyebrow)) {
    missing.push({ key: 'eyebrow', message: TEMPLATE3_MISSING_MESSAGES.eyebrow })
  }
  if (!htmlHasVisibleText(input.template3_about_title)) {
    missing.push({ key: 'aboutTitle', message: TEMPLATE3_MISSING_MESSAGES.aboutTitle })
  }
  if (!htmlHasVisibleText(input.template3_about_text)) {
    missing.push({ key: 'aboutText', message: TEMPLATE3_MISSING_MESSAGES.aboutText })
  }

  const advantages = normalizeTemplate3Advantages(input.template3_advantages)
  advantages.forEach((item, index) => {
    if (!htmlHasVisibleText(item.title) || !htmlHasVisibleText(item.description)) {
      missing.push({
        key: advantageFieldKey(index),
        message: TEMPLATE3_MISSING_MESSAGES.advantageText,
      })
    }
  })

  const faq = normalizeTemplate3Faq(input.template3_faq)
  if (faq.some((item) => !htmlHasVisibleText(item.question) || !htmlHasVisibleText(item.answer))) {
    missing.push({ key: 'faq-1', message: TEMPLATE3_MISSING_MESSAGES.faq })
    missing.push({ key: 'faq-2', message: TEMPLATE3_MISSING_MESSAGES.faq })
    missing.push({ key: 'faq-3', message: TEMPLATE3_MISSING_MESSAGES.faq })
  }

  if (!hasImage(input.template3_hero_background, input.template3_hero_background_url)) {
    missing.push({ key: 'heroBackground', message: TEMPLATE3_MISSING_MESSAGES.heroBackground })
  }
  if (!hasImage(input.template3_about_image, input.template3_about_image_url)) {
    missing.push({ key: 'aboutImage', message: TEMPLATE3_MISSING_MESSAGES.aboutImage })
  }
  if (!hasImage(input.template3_floating_cta_image, input.template3_floating_cta_image_url)) {
    missing.push({ key: 'floatingCta', message: TEMPLATE3_MISSING_MESSAGES.floatingCta })
  }

  const advantageFiles = [
    input.template3_advantage_image_1,
    input.template3_advantage_image_2,
    input.template3_advantage_image_3,
  ]
  advantages.forEach((item, index) => {
    const key = advantageFieldKey(index)
    if (missing.some((entry) => entry.key === key)) {
      return
    }
    if (!hasImage(advantageFiles[index], item.image_url)) {
      missing.push({ key, message: TEMPLATE3_MISSING_MESSAGES.advantageImage })
    }
  })

  return missing
}

/** Valida o conteúdo obrigatório do Template 3 no painel. Mensagens em PT-BR. */
export function validateTemplate3CampaignContent(input: Template3CampaignValidationInput): string | null {
  return listMissingTemplate3Fields(input)[0]?.message ?? null
}
